import { useCallback, useMemo, useRef, useState } from 'react';
import { startOfMonth } from 'date-fns';
import type { Event } from '@flock-types/events.types';
import { useAuth } from '@/context/AuthContext';
import { hasRoleAtLeast } from '@/components/layout/RoleGate';
import CalendarGrid from './calendar/CalendarGrid';
import CalendarToolbar, { type CalendarView } from './calendar/CalendarToolbar';
import CalendarSidebar from './calendar/CalendarSidebar';
import AgendaList from './calendar/AgendaList';
import EventHoverCard from './calendar/EventHoverCard';
import { useCalendarEvents, type HiddenCalendars } from './calendar/useCalendarEvents';
import type { CalendarEventInstance } from './calendar/types';
import EventSlideOver, { type SlideOverState } from './EventSlideOver';
import EventFormModal from './EventFormModal';

const LAST_VIEW_KEY = 'flock_events_last_view';
const HIDDEN_KEY = 'flock_events_hidden_calendars';
const HOVER_OPEN_DELAY_MS = 250;
const HOVER_CLOSE_GRACE_MS = 150;

function loadHidden(): HiddenCalendars {
  try {
    const raw = JSON.parse(localStorage.getItem(HIDDEN_KEY) ?? '{}');
    return {
      orgIds: new Set(Array.isArray(raw.orgIds) ? raw.orgIds : []),
      groupIds: new Set(Array.isArray(raw.groupIds) ? raw.groupIds : []),
    };
  } catch {
    return { orgIds: new Set(), groupIds: new Set() };
  }
}

interface HoverCardState {
  instance: CalendarEventInstance;
  anchorRect: { top: number; bottom: number; left: number; right: number };
}

export default function EventsPage() {
  const { currentOrganization, currentGroup, organizations, role } = useAuth();

  const memberGroups = useMemo(
    () =>
      organizations.find((o) => o.organization_id === currentOrganization?.id)?.groups ?? [],
    [organizations, currentOrganization?.id]
  );

  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [view, setView] = useState<CalendarView>(() =>
    localStorage.getItem(LAST_VIEW_KEY) === 'agenda' ? 'agenda' : 'month'
  );
  const [hidden, setHidden] = useState<HiddenCalendars>(loadHidden);
  const [slideOver, setSlideOver] = useState<SlideOverState | null>(null);
  const [creating, setCreating] = useState<{ date?: Date } | null>(null);
  const [editing, setEditing] = useState<{ event: Event; organizationId?: string } | null>(null);

  // Hover preview (desktop only — touch goes straight to the slide-over)
  const [hoverCard, setHoverCard] = useState<HoverCardState | null>(null);
  const hoverOpenTimer = useRef<number | null>(null);
  const hoverCloseTimer = useRef<number | null>(null);

  const { eventsByDay, sidebarOrgs, isLoading, isError, refetch } = useCalendarEvents(
    month,
    hidden.orgIds.size + hidden.groupIds.size > 0 ? hidden : null
  );

  const isLeader = hasRoleAtLeast(role, 'group_leader');
  // Group context shown on chips/rows when the calendar spans >1 group anywhere
  const showGroup = sidebarOrgs.reduce((n, o) => n + o.groups.length, 0) > 1;

  const handleHiddenChange = (next: HiddenCalendars) => {
    setHidden(next);
    localStorage.setItem(
      HIDDEN_KEY,
      JSON.stringify({ orgIds: [...next.orgIds], groupIds: [...next.groupIds] })
    );
  };

  const clearHoverTimers = () => {
    if (hoverOpenTimer.current) window.clearTimeout(hoverOpenTimer.current);
    if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current);
    hoverOpenTimer.current = null;
    hoverCloseTimer.current = null;
  };

  const handleChipMouseEnter = useCallback(
    (instance: CalendarEventInstance, e: React.MouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      clearHoverTimers();
      hoverOpenTimer.current = window.setTimeout(() => {
        setHoverCard({
          instance,
          anchorRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
        });
      }, HOVER_OPEN_DELAY_MS);
    },
    []
  );

  const handleChipMouseLeave = useCallback(() => {
    if (hoverOpenTimer.current) window.clearTimeout(hoverOpenTimer.current);
    hoverCloseTimer.current = window.setTimeout(() => setHoverCard(null), HOVER_CLOSE_GRACE_MS);
  }, []);

  const keepHoverCard = useCallback(() => {
    if (hoverCloseTimer.current) window.clearTimeout(hoverCloseTimer.current);
  }, []);

  const closeHoverCard = useCallback(() => setHoverCard(null), []);

  const handleViewChange = (next: CalendarView) => {
    setView(next);
    localStorage.setItem(LAST_VIEW_KEY, next);
  };

  const handleSelectEvent = (instance: CalendarEventInstance) => {
    clearHoverTimers();
    setHoverCard(null);
    setSlideOver({
      kind: 'event',
      eventId: instance.id,
      organizationId: instance.organizationId,
      occurrenceStart: instance.occurrenceStart,
      isVirtual: instance.isVirtual,
      groupId: instance.groupId,
      groupName: showGroup ? instance.groupName : undefined,
    });
  };

  const handleSelectDay = (date: Date) => {
    clearHoverTimers();
    setHoverCard(null);
    setSlideOver({ kind: 'day', date });
  };

  if (organizations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
        Join a group to see events.
      </div>
    );
  }

  return (
    <div className="flex h-full">
      <div className="hidden lg:flex">
        <CalendarSidebar orgs={sidebarOrgs} hidden={hidden} onChange={handleHiddenChange} />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="shrink-0 border-b border-flock-gray-200 bg-white px-6 py-4">
          <CalendarToolbar
            month={month}
            onMonthChange={setMonth}
            onToday={() => setMonth(startOfMonth(new Date()))}
            view={view}
            onViewChange={handleViewChange}
            onNewEvent={() => setCreating({})}
          />
        </header>

        <div className="min-h-0 flex-1 px-6 py-4">
          {isError && (
            <div className="py-4 text-center text-sm text-red-600">
              Couldn&apos;t load events.{' '}
              <button onClick={() => refetch()} className="font-medium underline">
                Try again
              </button>
            </div>
          )}

          {isLoading && !isError && (
            <div className="h-full animate-pulse rounded-2xl border border-flock-gray-200 bg-white" />
          )}

          {!isLoading && !isError && (
            <>
              {view === 'month' && (
                <div className="hidden h-full lg:block">
                  <CalendarGrid
                    month={month}
                    eventsByDay={eventsByDay}
                    onSelectEvent={handleSelectEvent}
                    onSelectDay={handleSelectDay}
                    onCreateOnDay={isLeader ? (date) => setCreating({ date }) : undefined}
                    onChipMouseEnter={handleChipMouseEnter}
                    onChipMouseLeave={handleChipMouseLeave}
                  />
                </div>
              )}
              {/* Agenda renders when selected, and always below lg (grid is unusable there) */}
              <div
                className={
                  view === 'agenda' ? 'h-full overflow-y-auto' : 'h-full overflow-y-auto lg:hidden'
                }
              >
                <AgendaList
                  eventsByDay={eventsByDay}
                  month={month}
                  onSelectEvent={handleSelectEvent}
                  showGroup={showGroup}
                  onNewEvent={isLeader ? () => setCreating({}) : undefined}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {hoverCard && !slideOver && (
        <EventHoverCard
          instance={hoverCard.instance}
          anchorRect={hoverCard.anchorRect}
          onMouseEnter={keepHoverCard}
          onMouseLeave={closeHoverCard}
        />
      )}

      {slideOver && (
        <EventSlideOver
          state={slideOver}
          eventsByDay={eventsByDay}
          onClose={() => setSlideOver(null)}
          onEdit={(event, organizationId) => setEditing({ event, organizationId })}
          showGroup={showGroup}
        />
      )}

      {creating && memberGroups.length > 0 && (
        <EventFormModal
          groupId={currentGroup?.id ?? memberGroups[0].id}
          groups={memberGroups.map((g) => ({ id: g.id, name: g.name }))}
          defaultDate={creating.date}
          onSaved={() => refetch()}
          onClose={() => setCreating(null)}
        />
      )}

      {editing && (
        <EventFormModal
          groupId={editing.event.group_id}
          event={editing.event}
          organizationId={editing.organizationId}
          onSaved={() => refetch()}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
